import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import realLogo from './assets/real-logo.png';
import Clients from './components/Clients';
import Deals from './components/Deals';
import ImportContacts from './components/ImportContacts';
import ConnectionStatus from './components/ConnectionStatus';
import Dashboard from './components/Dashboard';
import EmployeeDashboard from './components/EmployeeDashboard';
import EmployeeManagement from './components/EmployeeManagement';
import Auth from './components/Auth';
import EmailVerify from './components/EmailVerify';
import AdminDashboard from './components/AdminDashboard';
import AccountInfo from './components/AccountInfo';
import Units from './components/Units';
import Properties from './components/Properties';
import WhatsApp from './components/WhatsApp';
import Settings from './components/Settings';
import Partners from './components/Partners';
import Events from './components/Events';
import EventLanding from './components/EventLanding';
import { ToastProvider } from './components/Toast';
import api from './utils/api';
import { ar, en } from './utils/translations';
import { COUNTRY_CODES } from './utils/countryCodes';
// React Icons - Colorful icons
import { FaChartBar, FaUsers, FaHandshake, FaUserFriends, FaUserCircle, FaBuilding, FaCity, FaWhatsapp, FaCommentDots, FaCog, FaCalendarAlt } from 'react-icons/fa';

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showImport, setShowImport] = useState(false);
  const [dashboardRefresh, setDashboardRefresh] = useState(0);
  // Conversation deep-link: set by Clients page WhatsApp button or the quick-convo button
  // { conversation: <conv object> } — consumed by WhatsApp → WhatsAppInbox, cleared after use
  const [pendingWhatsAppConv, setPendingWhatsAppConv] = useState(null);
  /** When opening Settings from WhatsApp token banner, land on WhatsApp sub-tab */
  const [settingsInitialTab, setSettingsInitialTab] = useState('organization');
  // Quick-convo modal: shown by the floating button, pre-fills phone for walk-in clients
  const [showQuickConvoModal, setShowQuickConvoModal] = useState(false);
  const [quickConvoPhone, setQuickConvoPhone] = useState('');
  const [quickConvoCountryCode, setQuickConvoCountryCode] = useState('+966');
  const [quickConvoLoading, setQuickConvoLoading] = useState(false);
  const [quickConvoError, setQuickConvoError] = useState(null);
  // First-time tooltip: shown once until dismissed, to introduce the button to existing users
  const [showQuickConvoTooltip, setShowQuickConvoTooltip] = useState(() => {
    try { return !localStorage.getItem('qc_tooltip_dismissed'); } catch (_) { return true; }
  });
  const quickConvoInputRef = useRef(null);
  const [language, setLanguage] = useState(() => {
    // Get saved language preference or default to Arabic
    return localStorage.getItem('language') || 'ar';
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const t = language === 'ar' ? ar : en;
  const isRTL = language === 'ar';
  
  // Determine user role
  const isOwner = userRole === 'owner';
  const isManager = userRole === 'manager';
  // Only consider employee if explicitly set to 'employee', otherwise default to owner
  const isEmployee = checkingAuth ? false : (userRole === 'employee');

  // Resolved permissions — owners/managers get all permissions; employees use their stored flags
  const userPermissions = (isOwner || isManager)
    ? null  // null means "full access — skip all checks"
    : (user?.permissions && typeof user.permissions === 'object' ? user.permissions : {});

  // Helper: check a single permission key (always true for owners/managers)
  const can = (key) => {
    if (isOwner || isManager) return true;
    if (!isEmployee) return true; // unauthenticated or owner-defaulted
    return !!userPermissions?.[key];
  };
  
  // Debug: Log role information (only when authenticated)
  useEffect(() => {
    if (isAuthenticated && !checkingAuth) {
      console.log('🔍 ROLE DEBUG:');
      console.log('   Current userRole state:', userRole);
      console.log('   User object from state:', user);
      console.log('   isOwner:', isOwner);
      console.log('   isManager:', isManager);
      console.log('   isEmployee:', isEmployee);
    }
  }, [userRole, isAuthenticated, checkingAuth, isOwner, isManager, isEmployee, user]);

  // Check if user is already logged in and verify token
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        // Clear any invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        setCheckingAuth(false);
        return;
      }

      // Verify token is valid by calling verify endpoint
      try {
        const response = await api.get('/auth/verify');
        if (response.data && response.data.user) {
          setIsAuthenticated(true);
          setUser(response.data.user);
          const role = response.data.user.role || 'owner';
          console.log('🔍 DEBUG - Verify endpoint response:');
          console.log('   Full response:', JSON.stringify(response.data, null, 2));
          console.log('   User object:', response.data.user);
          console.log('   Role value:', response.data.user.role);
          console.log('   Role type:', typeof response.data.user.role);
          console.log('   Final role used:', role);
          
          setUserRole(role);
          // Update localStorage with fresh user data (including role)
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          // If role is null/undefined, set it to owner (default)
          if (!response.data.user.role || response.data.user.role === 'null') {
            console.warn('⚠️  WARNING: User role is null/undefined in database response! Setting to owner.');
            setUserRole('owner');
            const updatedUser = { ...response.data.user, role: 'owner' };
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        } else {
          // Invalid token, clear it
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        }
      } catch (error) {
        // Token is invalid or expired, clear it
        console.error('Token verification failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    console.log('🔍 handleLogin called with userData:', JSON.stringify(userData, null, 2));
    setIsAuthenticated(true);
    setUser(userData);
    
    // Get role from userData - don't default to employee, use what comes from server
    const role = userData.role;
    console.log('🔍 Login - Role from server:', role, 'Type:', typeof role);
    
    if (!role || role === 'null' || role === null) {
      console.warn('⚠️  WARNING: No role in login response! Defaulting to owner for existing users.');
      const updatedUser = { ...userData, role: 'owner', permissions: userData.permissions || {} };
      setUserRole('owner');
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } else {
      setUserRole(role);
      // Ensure permissions are preserved in local state
      const userWithPerms = { ...userData, permissions: userData.permissions || {} };
      setUser(userWithPerms);
      localStorage.setItem('user', JSON.stringify(userWithPerms));
      console.log('✅ Login - User role set to:', role, '| permissions:', userWithPerms.permissions);
    }
  };

  const handleUserUpdate = (updatedUser) => {
    if (!updatedUser) return;
    setUser(updatedUser);
    const role = updatedUser.role || 'owner';
    setUserRole(role);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    setUserRole(null);
  };

  const handleQuickConvoSubmit = async () => {
    const codeDigits = quickConvoCountryCode.replace(/\D/g, '');
    // Strip leading trunk zero (e.g. "0568019465" → "568019465") before combining
    const localDigits = String(quickConvoPhone || '').replace(/\D/g, '').replace(/^0+/, '');
    if (!localDigits) return;
    const normalizedPhone = codeDigits + localDigits;
    setQuickConvoLoading(true);
    setQuickConvoError(null);
    try {
      const { createOrGetConversation } = await import('./utils/api');
      const res = await createOrGetConversation(normalizedPhone);
      const conv = res.data;
      setShowQuickConvoModal(false);
      setQuickConvoPhone('');
      setPendingWhatsAppConv(conv);
      setActiveTab('whatsapp');
    } catch (err) {
      const msg = err?.response?.data?.error;
      setQuickConvoError(
        msg || (language === 'ar'
          ? 'تعذّر فتح المحادثة. تحقق من صحة الرقم وحاول مرة أخرى.'
          : 'Could not open conversation. Check the number and try again.')
      );
    } finally {
      setQuickConvoLoading(false);
    }
  };

  // Check if accessing admin route - must be checked FIRST before auth
  const isAdminRoute = window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin');
  const isVerifyEmailRoute =
    window.location.pathname === '/verify-email' || window.location.pathname.startsWith('/verify-email');
  const isEventLandingRoute =
    window.location.pathname === '/e' || window.location.pathname.startsWith('/e/');

  // Email verification landing (public)
  if (isVerifyEmailRoute) {
    return <EmailVerify language={language} />;
  }

  // Public event QR registration (no auth)
  if (isEventLandingRoute) {
    return <EventLanding />;
  }

  // Show admin dashboard if on admin route (skip all auth checks)
  if (isAdminRoute) {
    return <AdminDashboard language={language} />;
  }

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
      </div>
    );
  }

  // Show login/register if not authenticated
  if (!isAuthenticated) {
    return <Auth onLogin={handleLogin} language={language} />;
  }

  return (
    <ToastProvider>
    <div className="App" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className={`app-header${user?.company_logo_url ? ' app-header--company' : ''}`}>
        <div className="container">
          <div
            className="logo-section"
            role="button"
            tabIndex={0}
            onClick={() => {
              setShowImport(false);
              setActiveTab('dashboard');
              setDashboardRefresh(prev => prev + 1);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setShowImport(false);
                setActiveTab('dashboard');
                setDashboardRefresh(prev => prev + 1);
              }
            }}
            aria-label={language === 'ar' ? 'العودة للوحة التحكم' : 'Go to dashboard'}
          >
            <img
              src={user?.company_logo_url || realLogo}
              alt={user?.business_name || 'Real'}
              className={`app-header-logo${user?.company_logo_url ? ' app-header-logo--company' : ''}`}
            />
            {!user?.company_logo_url && (
              <p className="subtitle">
                {user?.business_name || t.subtitle}
              </p>
            )}
          </div>
          <div className="header-right">
            <div className="user-controls">
              <button 
                className="logout-btn" 
                onClick={handleLogout}
              >
                {language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
              </button>
              <div className="language-selector">
                <span>{t.language}:</span>
                <select
                  value={language}
                  onChange={(e) => {
                    const newLanguage = e.target.value;
                    setLanguage(newLanguage);
                    localStorage.setItem('language', newLanguage);
                  }}
                >
                  <option value="ar">{t.arabic}</option>
                  <option value="en">{t.english}</option>
                </select>
              </div>
            </div>
            <div className="account-group">
              <span className="user-name-with-role">
                {user?.name || ''}
                {userRole && (
                  <span className="user-role-inline">
                    {isRTL ? ' (' : ' ('}
                {userRole === 'owner' ? (language === 'ar' ? 'مالك' : 'Owner') :
                 userRole === 'manager' ? (language === 'ar' ? 'مدير' : 'Manager') :
                 (language === 'ar' ? 'موظف' : 'Employee')}
                    {isRTL ? ') ' : ')'}
                  </span>
                )}
              </span>
              <button
                className="account-btn"
                onClick={() => setActiveTab('account')}
                title={t.account || (language === 'ar' ? 'الحساب' : 'Account')}
                aria-label={t.account || (language === 'ar' ? 'الحساب' : 'Account')}
              >
                <FaUserCircle />
              </button>
            </div>
          </div>
        </div>
      </header>

      <nav className="main-nav">
        <div className="container">
          {/* 1. Dashboard - Always first for all roles */}
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => {
              setActiveTab('dashboard');
              setDashboardRefresh(prev => prev + 1);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <FaChartBar style={{ color: '#ea580c', fontSize: '18px' }} />
            {t.dashboard}
          </button>
          
          {/* Each tab is gated by its own permission check.
              Employees with no permissions see only Dashboard (their home).
              Employees with specific permissions see exactly those tabs. */}

          {/* 2. Properties — Owner/Manager OR employee with properties_view */}
          {(isOwner || isManager || can('properties_view')) && (
            <button 
              className={activeTab === 'properties' ? 'active' : ''}
              onClick={() => setActiveTab('properties')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaCity style={{ color: '#f97316', fontSize: '18px' }} />
              {language === 'ar' ? 'العقارات' : 'Properties'}
            </button>
          )}

          {/* 3. Units — Owner/Manager OR employee with units_view */}
          {(isOwner || isManager || can('units_view')) && (
            <button 
              className={activeTab === 'units' ? 'active' : ''}
              onClick={() => setActiveTab('units')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaBuilding style={{ color: '#f97316', fontSize: '18px' }} />
              {language === 'ar' ? 'الوحدات' : 'Units'}
            </button>
          )}

          {/* Team — Owner/Manager only (employees are managed here, not managing) */}
          {(isOwner || isManager) && (
            <button 
              className={activeTab === 'team' ? 'active' : ''}
              onClick={() => setActiveTab('team')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaUserFriends style={{ color: '#fdba74', fontSize: '18px' }} />
              {language === 'ar' ? 'الفريق' : 'Team'}
            </button>
          )}

          {/* 4. Deals — Owner/Manager OR employee with deals_view */}
          {(isOwner || isManager || can('deals_view')) && (
            <button 
              className={activeTab === 'deals' ? 'active' : ''}
              onClick={() => setActiveTab('deals')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaHandshake style={{ color: '#fdba74', fontSize: '18px' }} />
              {t.deals || 'Deals'}
            </button>
          )}

          {/* 5. Clients — Owner OR employee with clients_view */}
          {(isOwner || can('clients_view')) && (
            <button 
              className={activeTab === 'clients' ? 'active' : ''}
              onClick={() => setActiveTab('clients')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaUsers style={{ color: '#f97316', fontSize: '18px' }} />
              {t.clients}
            </button>
          )}

          {/* Partners (broker signups from website) */}
          {(isOwner || isManager || can('clients_view')) && (
            <button
              className={activeTab === 'partners' ? 'active' : ''}
              onClick={() => setActiveTab('partners')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaUserFriends style={{ color: '#0ea5e9', fontSize: '18px' }} />
              {language === 'ar' ? 'شركاء' : 'Partners'}
            </button>
          )}

          {/* Events — QR / fair ops */}
          {(isOwner || isManager || can('clients_view')) && (
            <button
              className={activeTab === 'events' ? 'active' : ''}
              onClick={() => setActiveTab('events')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaCalendarAlt style={{ color: '#d97706', fontSize: '18px' }} />
              {language === 'ar' ? 'فعاليات' : 'Events'}
            </button>
          )}

          {/* 6. WhatsApp — Owner/Manager OR employee with wa_inbox */}
          {(isOwner || isManager || can('wa_inbox')) && (
            <button 
              className={activeTab === 'whatsapp' ? 'active' : ''}
              onClick={() => setActiveTab('whatsapp')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaWhatsapp style={{ color: '#25D366', fontSize: '18px' }} />
              {language === 'ar' ? 'واتساب' : 'WhatsApp'}
            </button>
          )}

          {/* Settings — Owner/Manager only */}
          {(isOwner || isManager) && (
            <button
              className={activeTab === 'settings' ? 'active' : ''}
              onClick={() => {
                setSettingsInitialTab('organization');
                setActiveTab('settings');
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaCog style={{ color: '#64748b', fontSize: '18px' }} />
              {language === 'ar' ? 'الإعدادات' : 'Settings'}
            </button>
          )}
        </div>
      </nav>

      {/* Full-width main = scroll works on beige side margins; inner .container keeps content width */}
      <main className="app-main">
        <div className={activeTab === 'whatsapp' ? 'container container--fullbleed' : 'container'}>
        <ConnectionStatus language={language} />
        {showImport ? (
          <ImportContacts 
            onClose={() => setShowImport(false)}
            onImportComplete={() => {
              setShowImport(false);
              setActiveTab('clients');
              setDashboardRefresh(prev => prev + 1);
            }}
            language={language}
          />
        ) : (
          <>
            {activeTab === 'dashboard' && (
              isEmployee ? (
                <EmployeeDashboard key={`employee-dashboard-${dashboardRefresh}`} language={language} userId={user?.id} department={user?.department} />
              ) : (
                <Dashboard key={`dashboard-${dashboardRefresh}`} language={language} onNavigate={setActiveTab} refreshTrigger={dashboardRefresh} userRole={userRole} />
              )
            )}
            {/* my-deals tab removed - employees now see everything on Dashboard */}
            {activeTab === 'properties' && (isOwner || isManager || can('properties_view')) && <Properties language={language} userRole={userRole} />}
            {activeTab === 'units' && (isOwner || isManager || can('units_view')) && <Units language={language} userRole={userRole} />}
            {activeTab === 'team' && (isOwner || isManager) && <EmployeeManagement language={language} userRole={userRole} />}
            {activeTab === 'deals' && (isOwner || isManager || can('deals_view')) && <Deals language={language} userRole={userRole} />}
            {activeTab === 'clients' && (isOwner || can('clients_view')) && (
              <Clients
                language={language}
                userRole={userRole}
                onOpenPhoneImport={isOwner ? () => setShowImport(true) : undefined}
                onNavigateToWhatsApp={(conv) => {
                  setPendingWhatsAppConv(conv);
                  setActiveTab('whatsapp');
                }}
              />
            )}
            {activeTab === 'partners' && (isOwner || isManager || can('clients_view')) && (
              <Partners language={language} />
            )}
            {activeTab === 'events' && (isOwner || isManager || can('clients_view')) && (
              <Events
                language={language}
                onNavigateToWhatsApp={(conv) => {
                  setPendingWhatsAppConv(conv);
                  setActiveTab('whatsapp');
                }}
              />
            )}
            {activeTab === 'whatsapp' && (isOwner || isManager || can('wa_inbox')) && (
              <WhatsApp
                language={language}
                userRole={userRole}
                userPermissions={userPermissions}
                currentUserId={user?.id}
                conversationToOpen={pendingWhatsAppConv}
                onConversationOpened={() => setPendingWhatsAppConv(null)}
                onOpenWhatsAppIntegrationSettings={
                  isOwner || isManager
                    ? () => {
                        setSettingsInitialTab('whatsapp');
                        setActiveTab('settings');
                      }
                    : undefined
                }
              />
            )}
            {activeTab === 'account' && (
              <AccountInfo
                language={language}
                user={user}
                onUserUpdate={handleUserUpdate}
                onClose={() => setActiveTab('dashboard')}
              />
            )}
            {activeTab === 'settings' && (isOwner || isManager) && (
              <Settings
                language={language}
                userRole={userRole}
                user={user}
                initialTab={settingsInitialTab}
                onUserUpdate={handleUserUpdate}
              />
            )}
          </>
        )}
        </div>
      </main>

      {/* ─── Quick-conversation floating button ───────────────────────────────
          Visible to anyone with wa_inbox access. Lives in App so it floats
          over every page, not just the WhatsApp tab. */}
      {isAuthenticated && (isOwner || isManager || can('wa_inbox')) && (
        <div style={{ position: 'fixed', bottom: 28, ...(isRTL ? { left: 28 } : { right: 28 }), zIndex: 8000 }}>
          {/* First-time tooltip bubble — auto-dismisses after user clicks anywhere on it */}
          {showQuickConvoTooltip && (
            <div
              style={{
                position: 'absolute',
                bottom: 62,
                ...(isRTL ? { left: 0 } : { right: 0 }),
                background: '#1e293b',
                color: '#f8fafc',
                borderRadius: '12px',
                padding: '12px 16px',
                width: 230,
                boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
                fontSize: '13px',
                lineHeight: 1.55,
                direction: isRTL ? 'rtl' : 'ltr',
                cursor: 'pointer'
              }}
              onClick={() => {
                try { localStorage.setItem('qc_tooltip_dismissed', '1'); } catch (_) {}
                setShowQuickConvoTooltip(false);
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>
                {language === 'ar' ? '💬 محادثة واتساب سريعة' : '💬 Quick WhatsApp'}
              </div>
              {language === 'ar'
                ? 'ادخل رقم هاتف أي عميل مباشرة وافتح المحادثة في الصندوق — بدون الحاجة لإضافته في العملاء أولاً.'
                : 'Enter any phone number and open the conversation in your inbox — no need to add them as a client first.'}
              <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
                {language === 'ar' ? 'انقر هنا للإغلاق' : 'Tap to dismiss'}
              </div>
              {/* Arrow pointing down */}
              <div style={{
                position: 'absolute', bottom: -8,
                ...(isRTL ? { left: 20 } : { right: 20 }),
                width: 0, height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid #1e293b'
              }} />
            </div>
          )}
          <button
            title={language === 'ar' ? 'محادثة واتساب سريعة' : 'Quick WhatsApp conversation'}
            onClick={() => {
              setShowQuickConvoTooltip(false);
              try { localStorage.setItem('qc_tooltip_dismissed', '1'); } catch (_) {}
              setQuickConvoPhone('');
              setQuickConvoError(null);
              setShowQuickConvoModal(true);
              setTimeout(() => quickConvoInputRef.current?.focus(), 80);
            }}
            style={{
              width: 56, height: 56,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              border: 'none',
              boxShadow: '0 4px 20px rgba(37,211,102,0.45)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'transform 0.15s, box-shadow 0.15s'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(37,211,102,0.55)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,211,102,0.45)'; }}
          >
            <FaCommentDots style={{ color: '#fff', fontSize: 24 }} />
          </button>
        </div>
      )}

      {/* ─── Quick-conversation modal ─────────────────────────────────────── */}
      {showQuickConvoModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => { if (!quickConvoLoading) setShowQuickConvoModal(false); }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 20, padding: '32px 28px',
              maxWidth: 400, width: '92%',
              boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <FaWhatsapp style={{ color: '#fff', fontSize: 22 }} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                  {language === 'ar' ? 'محادثة واتساب سريعة' : 'Quick WhatsApp'}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  {language === 'ar'
                    ? 'ادخل رقم الهاتف لفتح المحادثة مباشرة في الصندوق'
                    : 'Enter a phone number to open the conversation in your inbox'}
                </div>
              </div>
            </div>

            {/* Phone input: country code dropdown + local number */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                {language === 'ar' ? 'رقم الهاتف' : 'Phone number'}
              </label>
              <div style={{ display: 'flex', gap: 8, direction: 'ltr' }}>
                <select
                  value={quickConvoCountryCode}
                  onChange={e => { setQuickConvoCountryCode(e.target.value); setQuickConvoError(null); }}
                  disabled={quickConvoLoading}
                  style={{
                    padding: '12px 8px', fontSize: 14, fontWeight: 600,
                    border: `1.5px solid ${quickConvoError ? '#ef4444' : '#d1d5db'}`,
                    borderRadius: 10, outline: 'none', background: quickConvoLoading ? '#f9fafb' : '#fff',
                    cursor: 'pointer', flexShrink: 0, minWidth: 90
                  }}
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
                <input
                  ref={quickConvoInputRef}
                  type="tel"
                  placeholder={language === 'ar' ? 'مثال: 501234567' : 'e.g. 501234567'}
                  value={quickConvoPhone}
                  onChange={e => { setQuickConvoPhone(e.target.value.replace(/\D/g, '')); setQuickConvoError(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleQuickConvoSubmit(); }}
                  disabled={quickConvoLoading}
                  style={{
                    flex: 1, padding: '12px 14px', fontSize: 15,
                    border: `1.5px solid ${quickConvoError ? '#ef4444' : '#d1d5db'}`,
                    borderRadius: 10, outline: 'none', boxSizing: 'border-box',
                    direction: 'ltr', textAlign: 'left',
                    background: quickConvoLoading ? '#f9fafb' : '#fff'
                  }}
                />
              </div>
            </div>

            {quickConvoError && (
              <div style={{
                fontSize: 13, color: '#dc2626', background: '#fef2f2',
                borderRadius: 8, padding: '10px 14px', marginBottom: 14
              }}>
                {quickConvoError}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <button
                onClick={handleQuickConvoSubmit}
                disabled={quickConvoLoading || !quickConvoPhone.trim()}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10, border: 'none',
                  background: (!quickConvoPhone.trim() || quickConvoLoading)
                    ? '#d1fae5' : 'linear-gradient(135deg, #25D366, #128C7E)',
                  color: (!quickConvoPhone.trim() || quickConvoLoading) ? '#6b7280' : '#fff',
                  fontWeight: 700, fontSize: 15, cursor: quickConvoLoading ? 'wait' : 'pointer',
                  transition: 'background 0.15s'
                }}
              >
                {quickConvoLoading
                  ? (language === 'ar' ? 'جارٍ الفتح…' : 'Opening…')
                  : (language === 'ar' ? 'فتح المحادثة' : 'Open conversation')}
              </button>
              <button
                onClick={() => setShowQuickConvoModal(false)}
                disabled={quickConvoLoading}
                style={{
                  padding: '12px 20px', borderRadius: 10,
                  border: '1.5px solid #e2e8f0', background: 'transparent',
                  color: '#64748b', fontWeight: 600, fontSize: 14, cursor: 'pointer'
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ToastProvider>
  );
}

export default App;
